import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  privateConversations,
  privateMessagesDashboardStats,
  privateMessagesRules,
  blockedUsersList,
  conversationTypeLabels,
  conversationTypeIcons,
  attachmentTypeConfig,
  getConversationById,
  getUnreadCountTotal,
  getPinnedConversations,
  getOnlineContacts,
  type PrivateConversation,
  type PrivateMessage,
  type ConversationType,
} from '@/mocks/rfwPrivateMessages';

type PageState = 'loading' | 'error' | 'ready';
type ViewPanel = 'list' | 'chat';

export default function MensajesPrivadosPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [viewPanel, setViewPanel] = useState<ViewPanel>('list');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ConversationType | 'todas'>('todas');
  const [searchQuery, setSearchQuery] = useState('');
  const [messageText, setMessageText] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeConversationId]);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const totalUnread = getUnreadCountTotal();
  const onlineContacts = getOnlineContacts();
  const pinnedConvs = getPinnedConversations();

  const activeConversation = activeConversationId ? getConversationById(activeConversationId) : undefined;

  const filteredConvs = useMemo(() => {
    let result = typeFilter === 'todas' ? privateConversations : privateConversations.filter((c) => c.type === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) =>
        c.participants.some((p) => p.name.toLowerCase().includes(q) || p.username.toLowerCase().includes(q)) ||
        c.lastMessage.toLowerCase().includes(q)
      );
    }
    return result;
  }, [typeFilter, searchQuery]);

  const openChat = useCallback((convId: string) => {
    setActiveConversationId(convId);
    setViewPanel('chat');
  }, []);

  const backToList = useCallback(() => {
    setViewPanel('list');
    setActiveConversationId(null);
    setMessageText('');
  }, []);

  const handleSendMessage = useCallback(() => {
    if (!messageText.trim()) return;
    setMessageText('');
  }, [messageText]);

  const handleBlock = useCallback(() => {
    setShowBlockModal(false);
  }, []);

  const handleReport = useCallback(() => {
    setShowReportModal(false);
  }, []);

  const handleDeleteMessage = useCallback((msgId: string) => {
    setShowDeleteConfirm(null);
  }, []);

  const typeFilters: { id: ConversationType | 'todas'; label: string; icon: string }[] = [
    { id: 'todas', label: 'Todas', icon: 'ri-message-2-line' },
    { id: 'oyente-oyente', label: 'Oyente↔Oyente', icon: conversationTypeIcons['oyente-oyente'] },
    { id: 'oyente-locutor', label: 'Oyente↔Locutor', icon: conversationTypeIcons['oyente-locutor'] },
    { id: 'oyente-moderador', label: 'Oyente↔Mod', icon: conversationTypeIcons['oyente-moderador'] },
    { id: 'locutor-locutor', label: 'Locutor↔Locutor', icon: conversationTypeIcons['locutor-locutor'] },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[200px] md:h-[260px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
            <div className="md:col-span-2 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar los mensajes</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar tus conversaciones. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20private%20messaging%20app%20interface%20concept%20warm%20ambient%20lighting%20soft%20gradient%20background%20abstract%20communication%20bubbles%20floating%20overlapping%20in%20elegant%20composition%20subtle%20rose%20and%20amber%20tones%20editorial%20product%20photography%20style%20clean%20aesthetic&width=1600&height=900&seq=rfw-mensajes-privados-hero&orientation=landscape"
          alt="Mensajes Privados RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-message-2-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Mensajes Privados
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Conversaciones directas con oyentes, locutores y moderadores de la comunidad RFW. Imágenes, audio, stickers y GIFs para conectar como nunca.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* MB WAYFINDER */}
        {viewPanel === 'chat' && (
          <button
            onClick={backToList}
            className="md:hidden flex items-center gap-1.5 text-sm font-semibold text-foreground-600 mb-4 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Volver a Conversaciones
          </button>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* LEFT COLUMN: CONVERSATION LIST */}
          <div className={`md:col-span-5 lg:col-span-4 ${viewPanel === 'chat' ? 'hidden md:block' : ''}`}>
            <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden">
              {/* SEARCH + STATS */}
              <div className="p-4 border-b border-background-200/50 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-extrabold text-foreground-950">Conversaciones</h2>
                  <div className="flex items-center gap-2">
                    {totalUnread > 0 && (
                      <span className="w-6 h-6 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{totalUnread}</span>
                    )}
                    <span className="text-[10px] text-foreground-500">{privateConversations.length} activas</span>
                  </div>
                </div>

                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    type="text"
                    placeholder="Buscar conversación..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 rounded-xl bg-background-100 border border-background-200/70 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <div className="flex gap-1 overflow-x-auto pb-1">
                  {typeFilters.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setTypeFilter(f.id)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        typeFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ONLINE CONTACTS */}
              {onlineContacts.length > 0 && (
                <div className="px-4 py-3 border-b border-background-200/50">
                  <div className="flex gap-2 overflow-x-auto">
                    {onlineContacts.map((contact) => (
                      <div key={contact.id} className="flex-shrink-0 flex flex-col items-center gap-1">
                        <div className="relative">
                          <img src={contact.photoUrl} alt={contact.name} className="w-10 h-10 rounded-full object-cover" />
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                        </div>
                        <p className="text-[9px] text-foreground-600 line-clamp-1 w-14 text-center">{contact.name.split(' ')[0]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CONVERSATION LIST */}
              <div className="overflow-y-auto max-h-[calc(100vh-460px)]">
                {pinnedConvs.length > 0 && (
                  <div className="px-4 pt-3">
                    <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1">Fijadas</p>
                    {pinnedConvs.map((conv) => (
                      <ConversationListItem
                        key={conv.id}
                        conversation={conv}
                        isActive={activeConversationId === conv.id}
                        onClick={() => openChat(conv.id)}
                      />
                    ))}
                  </div>
                )}

                <div className="px-4 py-3">
                  {filteredConvs.length > 0 ? (
                    filteredConvs.map((conv) => (
                      <ConversationListItem
                        key={conv.id}
                        conversation={conv}
                        isActive={activeConversationId === conv.id}
                        onClick={() => openChat(conv.id)}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <i className="ri-message-2-line text-2xl text-foreground-300 mb-2 block"></i>
                      <p className="text-xs text-foreground-400">No se encontraron conversaciones</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: CHAT AREA */}
          <div className={`md:col-span-7 lg:col-span-8 ${viewPanel === 'list' ? 'hidden md:flex md:items-center md:justify-center' : ''}`}>
            {!activeConversation ? (
              <div className="w-full h-[500px] rounded-2xl bg-background-50 border border-background-200/70 flex flex-col items-center justify-center px-6 text-center">
                <div className="w-20 h-20 rounded-3xl bg-rose-50 flex items-center justify-center mb-5">
                  <i className="ri-message-2-line text-3xl text-rose-400"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Tus Mensajes Privados</h3>
                <p className="text-sm text-foreground-500 max-w-sm mb-6">
                  Seleccioná una conversación para chatear. Recordá: los saludos y pedidos de canciones van por la Cabina, no por MD.
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-xs">
                  {typeFilters.filter((f) => f.id !== 'todas').slice(0, 4).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setTypeFilter(f.id)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-600 hover:border-rose-300/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${f.icon} text-[10px]`}></i>
                      {f.label.replace('↔', ' ↔ ')}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ChatPanel
                conversation={activeConversation}
                messageText={messageText}
                setMessageText={setMessageText}
                onSend={handleSendMessage}
                onBlock={() => setShowBlockModal(true)}
                onReport={() => setShowReportModal(true)}
                onDeleteMessage={handleDeleteMessage}
                showDeleteConfirm={showDeleteConfirm}
                setShowDeleteConfirm={setShowDeleteConfirm}
                chatEndRef={chatEndRef}
              />
            )}
          </div>
        </div>

        {/* RULES SECTION */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {privateMessagesRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </section>

        {/* BLOCKED USERS */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-forbid-line text-red-500"></i>
              Usuarios Bloqueados
              <span className="text-[11px] text-foreground-400 font-normal">({blockedUsersList.length})</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {blockedUsersList.map((user) => (
              <div key={user.id} className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70">
                <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover opacity-50 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{user.name}</p>
                  <p className="text-[10px] text-foreground-500 line-clamp-1">{user.reason}</p>
                  <p className="text-[9px] text-foreground-400 mt-0.5">{user.blockedDate}</p>
                </div>
                <button className="shrink-0 px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200 text-[9px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                  Desbloquear
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ATTACHMENT TYPES */}
        <section className="mt-8 mb-6">
          <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-attachment-2 text-rose-500"></i>
            Tipos de Adjuntos Soportados
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(attachmentTypeConfig).map(([key, config]) => (
              <div key={key} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className={`w-9 h-9 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-2`}>
                  <i className={`${config.icon} text-sm ${config.color}`}></i>
                </div>
                <p className="text-xs font-bold text-foreground-900">{config.label}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">
                  {key === 'imagen' && 'Fotos y capturas'}
                  {key === 'audio' && 'Notas de voz'}
                  {key === 'sticker' && 'Stickers RFW'}
                  {key === 'gif' && 'Animaciones'}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Link to="/centro-notificaciones" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-notification-3-line text-base text-rose-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Centro de Notificaciones</p>
            <p className="text-[10px] text-foreground-500 mt-1">No te pierdas nada de tu red</p>
          </Link>
          <Link to="/seguir-usuarios" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-group-line text-base text-amber-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Seguir Usuarios</p>
            <p className="text-[10px] text-foreground-500 mt-1">Conectá con más personas</p>
          </Link>
          <Link to="/perfil-social-oyente" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-violet-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-line text-base text-violet-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Perfil Social</p>
            <p className="text-[10px] text-foreground-500 mt-1">Tu identidad en la comunidad RFW</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-broadcast-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Nunca interferir con la Cabina.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Los mensajes privados son tuyos, personales y directos. Pero la magia de la radio está en el vivo, en la Cabina, en lo compartido. Saludos, peticiones de canciones, dedicatorias y todo lo que merece ser escuchado por todos va por la Cabina. Los MD son para conversaciones privadas, no para reemplazar lo que hace única a la radio: la experiencia de compartir en comunidad, en tiempo real, con el DJ poniendo la música y la gente participando junta.
          </p>
        </section>
      </main>

      {/* ─── MODAL BLOQUEAR ─── */}
      {showBlockModal && activeConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBlockModal(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-forbid-line text-2xl text-red-500"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">¿Bloquear a {activeConversation.participants[0].name}?</h3>
              <p className="text-xs text-foreground-500 mb-6">
                No recibirás más mensajes de esta persona y la conversación se archivará. Podés desbloquearla cuando quieras.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 font-semibold text-xs hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBlock}
                  className="flex-1 px-4 py-2.5 rounded-full bg-red-500 text-white font-semibold text-xs hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Bloquear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL REPORTAR ─── */}
      {showReportModal && activeConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReportModal(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-2xl text-amber-500"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Reportar Conversación</h3>
              <p className="text-xs text-foreground-500 mb-4">
                ¿Qué tipo de problema estás teniendo con {activeConversation.participants[0].name}?
              </p>
              <div className="space-y-2 mb-5 text-left">
                {['Mensajes ofensivos o inapropiados', 'Spam o autopromoción', 'Acoso o intimidación', 'Suplantación de identidad', 'Otro motivo'].map((reason) => (
                  <label key={reason} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-background-100 hover:bg-background-200/70 cursor-pointer transition-colors">
                    <input type="radio" name="reportReason" className="w-4 h-4 text-primary-500 accent-primary-500" />
                    <span className="text-xs text-foreground-700">{reason}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 font-semibold text-xs hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReport}
                  className="flex-1 px-4 py-2.5 rounded-full bg-primary-500 text-white font-semibold text-xs hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Enviar Reporte
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationListItem({
  conversation,
  isActive,
  onClick,
}: {
  conversation: PrivateConversation;
  isActive: boolean;
  onClick: () => void;
}) {
  const other = conversation.participants[0];

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 mb-1 rounded-xl transition-all duration-200 cursor-pointer ${
        isActive
          ? 'bg-rose-50 border border-rose-200/60'
          : 'hover:bg-background-100 border border-transparent'
      }`}
    >
      <div className="relative shrink-0">
        <img src={other.photoUrl} alt={other.name} className="w-11 h-11 rounded-full object-cover" />
        {other.isOnline && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{other.name}</p>
          <span className="text-[10px] text-foreground-400 shrink-0 ml-2">{conversation.lastMessageRelative}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-foreground-500 line-clamp-1 pr-2">{conversation.lastMessage}</p>
          {conversation.unreadCount > 0 && (
            <span className="shrink-0 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
      {conversation.isPinned && (
        <i className="ri-pushpin-fill text-[10px] text-rose-400 shrink-0"></i>
      )}
    </div>
  );
}

function ChatPanel({
  conversation,
  messageText,
  setMessageText,
  onSend,
  onBlock,
  onReport,
  onDeleteMessage,
  showDeleteConfirm,
  setShowDeleteConfirm,
  chatEndRef,
}: {
  conversation: PrivateConversation;
  messageText: string;
  setMessageText: (v: string) => void;
  onSend: () => void;
  onBlock: () => void;
  onReport: () => void;
  onDeleteMessage: (id: string) => void;
  showDeleteConfirm: string | null;
  setShowDeleteConfirm: (v: string | null) => void;
  chatEndRef: React.RefObject<HTMLDivElement | null>;
}) {
  const other = conversation.participants[0];

  const getTypeBadge = (type: ConversationType) => {
    if (type === 'oyente-oyente') return 'bg-emerald-100 text-emerald-700';
    if (type === 'oyente-locutor') return 'bg-amber-100 text-amber-700';
    if (type === 'oyente-moderador') return 'bg-rose-100 text-rose-700';
    return 'bg-violet-100 text-violet-700';
  };

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden flex flex-col h-[550px] md:h-[650px]">
      {/* CHAT HEADER */}
      <div className="flex items-center justify-between p-4 border-b border-background-200/50 shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={other.photoUrl} alt={other.name} className="w-10 h-10 rounded-full object-cover" />
            {other.isOnline && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-bold text-foreground-950">{other.name}</p>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${getTypeBadge(conversation.type)}`}>
                {conversationTypeLabels[conversation.type].split(' ↔ ')[0]}
              </span>
            </div>
            <p className="text-[10px] text-foreground-500">
              {other.isTyping ? 'Escribiendo...' : other.isOnline ? 'En línea' : other.lastSeen}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onReport}
            className="w-8 h-8 rounded-full bg-background-100 hover:bg-amber-50 flex items-center justify-center transition-colors cursor-pointer"
            title="Reportar"
          >
            <i className="ri-alert-line text-xs text-foreground-500 hover:text-amber-600"></i>
          </button>
          <button
            onClick={onBlock}
            className="w-8 h-8 rounded-full bg-background-100 hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer"
            title="Bloquear"
          >
            <i className="ri-forbid-line text-xs text-foreground-500 hover:text-red-600"></i>
          </button>
        </div>
      </div>

      {/* MESSAGES */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {conversation.messages.map((msg) => {
          const isMe = msg.senderId === conversation.participants[1].id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex items-end gap-2 max-w-[75%] ${isMe ? 'flex-row-reverse' : ''}`}>
                {!isMe && (
                  <img src={msg.senderPhoto} alt={msg.senderName} className="w-7 h-7 rounded-full object-cover shrink-0" />
                )}
                <div className="group relative">
                  {msg.isDeleted ? (
                    <div className={`px-3.5 py-2 rounded-2xl text-xs italic text-foreground-400 bg-background-100`}>
                      Mensaje eliminado
                    </div>
                  ) : (
                    <div className={`px-3.5 py-2 rounded-2xl text-xs leading-relaxed ${
                      isMe
                        ? 'bg-primary-500 text-white rounded-br-md'
                        : 'bg-background-100 text-foreground-800 rounded-bl-md'
                    }`}>
                      {msg.attachment && (
                        <div className="mb-2 -mx-1">
                          {msg.attachment.type === 'imagen' && (
                            <div className="rounded-xl overflow-hidden bg-black/5">
                              <img
                                src={msg.attachment.url}
                                alt={msg.attachment.name || 'Imagen'}
                                className="w-full max-w-[240px] object-cover"
                                style={{ maxHeight: '180px' }}
                              />
                              {msg.attachment.name && (
                                <p className="text-[9px] px-2 py-1 text-foreground-400 truncate">{msg.attachment.name}</p>
                              )}
                            </div>
                          )}
                          {msg.attachment.type === 'audio' && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background-50/50">
                              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                                <i className="ri-mic-line text-rose-500 text-sm"></i>
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-foreground-700 line-clamp-1">{msg.attachment.name}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] text-foreground-400">{msg.attachment.duration}</span>
                                  <div className="w-16 h-1 rounded-full bg-background-200">
                                    <div className="w-1/2 h-full rounded-full bg-rose-400"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          {(msg.attachment.type === 'sticker' || msg.attachment.type === 'gif') && (
                            <div className="rounded-xl overflow-hidden bg-transparent">
                              <img
                                src={msg.attachment.url}
                                alt={msg.attachment.name || (msg.attachment.type === 'sticker' ? 'Sticker' : 'GIF')}
                                className="w-full max-w-[160px] object-cover"
                                style={{ maxHeight: '160px' }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {msg.text && <p>{msg.text}</p>}
                      {msg.isReported && (
                        <span className="text-[9px] text-amber-200 mt-1 block">Reportado</span>
                      )}
                    </div>
                  )}
                  <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-[9px] text-foreground-400">{msg.timestampRelative}</span>
                    {isMe && !msg.isDeleted && (
                      <button
                        onClick={() => setShowDeleteConfirm(msg.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-[10px] text-foreground-400 hover:text-red-500"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef}></div>
      </div>

      {/* MESSAGE INPUT */}
      <div className="p-3 border-t border-background-200/50 shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-sky-50 flex items-center justify-center transition-colors cursor-pointer" title="Adjuntar imagen">
              <i className="ri-image-line text-xs text-foreground-500 hover:text-sky-600"></i>
            </button>
            <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-rose-50 flex items-center justify-center transition-colors cursor-pointer" title="Nota de voz">
              <i className="ri-mic-line text-xs text-foreground-500 hover:text-rose-600"></i>
            </button>
            <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-amber-50 flex items-center justify-center transition-colors cursor-pointer" title="Sticker">
              <i className="ri-emotion-laugh-line text-xs text-foreground-500 hover:text-amber-600"></i>
            </button>
            <button className="w-8 h-8 rounded-full bg-background-100 hover:bg-violet-50 flex items-center justify-center transition-colors cursor-pointer" title="GIF">
              <i className="ri-file-gif-line text-xs text-foreground-500 hover:text-violet-600"></i>
            </button>
          </div>
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Escribí un mensaje..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
              className="w-full pl-4 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            onClick={onSend}
            disabled={!messageText.trim()}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer ${
              messageText.trim()
                ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm'
                : 'bg-background-100 text-foreground-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-send-plane-fill text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}